import Footer from "./Footer";
import Navbar from "./Navbar";
import SEO from "./SEO";


const FairPracticeCode: React.FC = () => {
  const policySections = [
    {
      title: `${import.meta.env.VITE_COMPANY_NBFC} (Brand Name: ${import.meta.env.VITE_COMPANY_NAME})`,
      content: `Reviewed by Board of Directors
Approved on: 15 April 2025

BACKGROUND

The Reserve Bank of India ("RBI") has issued guidelines on Fair Practice Code for Non-Banking Finance Companies (NBFCs) vide Notification No. DNBS (PD) CC No. 80 / 03.10.042 / 2005-06 dated September 28, 2006 and amended from time to time, setting standards for fair business and corporate practices while dealing with customers.

${import.meta.env.VITE_COMPANY_NAME} adopts these guidelines and makes necessary modifications to this Code as required from time to time. The Company is committed to offering all financial products to eligible applicants without discrimination on grounds of race, caste, color, religion, sex, marital status, age, or disability.

The Fair Practice Code (FPC) outlines procedures for business transactions and ensures fair treatment of borrowers.`,
    },
    {
      title: "A. APPLICATIONS FOR LOANS AND THEIR PROCESSING",
      list: [
        "1. Borrower Communications: All borrower communications will be in the vernacular language or in a language understood by the borrower.",
        "2. Loan Application Forms: Loan application forms will include all necessary information impacting the borrower's interest, enabling informed decision-making. Forms will clearly list required documents.",
        "3. Application Acknowledgment: The Company shall provide an acknowledgment for all loan applications, indicating a timeframe within which a decision will be conveyed.",
      ],
    },
    {
      title: "B. LOAN APPRAISAL AND TERMS/CONDITIONS",
      list: [
        "1. Communication of Terms: The Company will communicate loan sanction details, including terms, conditions, annualized interest rate, and penal charges, in writing and in the language understood by the borrower.",
        "2. Documentation: The borrower's acceptance of terms and conditions will be retained. A copy of the loan agreement and all referenced documents will be furnished at the time of disbursement.",
      ],
    },
    {
      title: "C. DISBURSEMENT OF LOANS AND CHANGES IN TERMS",
      list: [
        "1. Changes to Terms: Any changes to terms, including interest rate, charges, or disbursement schedule, will be communicated in advance in the borrower's language. Changes will apply only prospectively.",
        "2. Loan Management: Loan recall or acceleration will align with the loan agreement. Securities will be released on repayment unless a legitimate lien exists. If so, the borrower will be informed with full particulars.",
      ],
    },
    {
      title: "D. RATE OF INTEREST",
      list: [
        "1. Interest Rate Determination: The Company will determine interest rates based on cost of funds, margin, and risk premium, with procedures for risk gradation.",
        "2. Transparency: Interest rate and rationale for variation among borrower categories will be disclosed in both application and sanction letter. Interest rate and gradation approach will be published on the Company website and updated as applicable.",
        "3. Annualized Terms: Interest will be expressed in annualized terms to ensure borrower clarity.",
      ],
    },
    {
      title: "E. GRIEVANCE REDRESSAL",
      list: [
        `1. Customer Care Department: Phone: ${import.meta.env.VITE_COMPANY_PHONE}, Email: ${import.meta.env.VITE_COMPANY_EMAIL}`,
        `2. Grievance Redressal Officer: Phone: ${import.meta.env.VITE_COMPANY_PHONE}, Email: ${import.meta.env.VITE_COMPANY_EMAIL}`,
        "3. RBI Contact: Officer-in-Charge, Department of Non-Banking Supervision, RBI, Delhi Regional Office, 6, Sansad Marg, New Delhi – 110001. Phone: +91-11-2371 4456 / 0538, +91-11-2375 2188, Email: dnbsnewdelhi@rbi.org.in",
      ],
      content: "Complaints will be addressed within one month. If unresolved, customers may contact RBI.",
    },
    {
      title: "F. GENERAL PROVISIONS",
      list: [
        "1. Borrower Affairs: The Company will avoid unnecessary interference in borrowers' affairs, except as per loan terms or newly disclosed information.",
        "2. Account Transfers: Borrowers requesting account transfers will receive a response within 21 days.",
        "3. Recovery Practices: Recovery practices will be conducted respectfully without harassment.",
        "4. Foreclosure Penalties: No foreclosure or prepayment penalties will be charged on floating rate loans to individual borrowers (non-business purposes).",
      ],
    },
    {
      title: "G. FORCE MAJEURE",
      content:
        "In extraordinary events, fulfillment of all commitments may not be possible. The Company shall make every effort to maintain service standards under such conditions.",
    },
    {
      title: "H. IMPLEMENTATION",
      content:
        "The implementation of the Fair Practice Code is the responsibility of the entire organization. It shall reflect through training, accountability, monitoring, audits, and effective use of technology. This Code shall be reviewed and updated periodically to enhance its relevance and value to borrowers.",
    },
    {
      title: "Place & Date",
      list: ["Place: New Delhi", "Date: 15 April 2025"],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary font-mono selection:bg-secondary selection:text-white ">
      <SEO
        title="Fair Practice Code"
        description={`Our commitment to fair business and corporate practices. ${import.meta.env.VITE_COMPANY_NAME} ensures transparent and equitable treatment for all borrowers.`}
        keywords={`fair practice code, NBFC compliance, ethical lending, ${import.meta.env.VITE_COMPANY_NAME} policies`}
      />
      <Navbar />

      {/* Official Registry Hero */}
      <section className="w-full bg-text-primary py-32 md:py-48 text-center px-6 relative overflow-hidden border-b-8 border-secondary mb-24">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-4 px-6 py-2 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.5em] mb-12">
            <div className="w-2 h-2 bg-secondary animate-pulse" />
            Fair_Practice_Protocol_v2.1
          </div>
          <h1 className="text-5xl md:text-[6rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase text-white italic">
            Fair Practice <span className="text-secondary not-italic">Code.</span>
          </h1>
          <p className="text-white/60 text-lg md:text-2xl max-w-3xl mx-auto font-bold leading-relaxed border-y border-white/10 py-10 uppercase tracking-tight">
            Institutional standards for customer equity. <br />
            <span className="text-white">Upholding transparency across all operations.</span>
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pb-40">
        <div className="border-t-4 border-text-primary">
          {policySections.map((section, idx) => (
            <div key={idx} className="border-b border-text-primary/10 py-16 group">
              <div className="flex flex-col gap-10">
                <div className="flex items-start gap-8">
                  <span className="text-secondary font-black text-xs pt-1">SEC_00{idx + 1}</span>
                  <h2 className="text-3xl md:text-5xl font-black text-text-primary leading-tight uppercase tracking-tighter italic">
                    {section.title}
                  </h2>
                </div>

                <div className="pl-16 md:pl-24 space-y-8">
                  {section.content && (
                    <p className="text-text-secondary font-bold text-lg leading-relaxed uppercase tracking-tight border-l-4 border-text-primary/5 pl-8 italic">
                      {section.content}
                    </p>
                  )}
                  {section.list && (
                    <ul className="space-y-6">
                      {section.list.map((li, i) => (
                        <li key={i} className="flex items-start gap-4 text-text-primary font-bold text-base leading-relaxed">
                          <div className="w-2 h-2 bg-secondary mt-2 shrink-0" />
                          <span className="uppercase tracking-tight">{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FairPracticeCode;
